import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function KpiSquadSocleFrontPRMergeTime() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState('');
  const [stats, setStats] = useState(null);
  const [prDetails, setPrDetails] = useState([]);

  const AUTHORS = [
    'cpindat@chapsvision.com',
    'lgrelet@chapsvision.com',
    'mchakhchoukh@chapsvision.com',
    'vleymonie@chapsvision.com'
  ];

  const REPO_NAME = 'argonos-front';

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      
      try {
        // Récupérer l'ID du repository
        setProgress('Récupération du repository...');
        const repoId = await azureDevOpsService.getRepositoryIdByName(REPO_NAME);
        
        if (!repoId) {
          setProgress(`Repository '${REPO_NAME}' non trouvé`);
          setLoading(false);
          return;
        }

        // Récupérer les PRs fusionnées des 6 derniers mois
        setProgress('Récupération des PRs fusionnées...');
        const prs = await azureDevOpsService.getMergedPullRequestsWithDetails(
          repoId,
          AUTHORS,
          6 // 6 derniers mois
        );

        if (prs.length === 0) {
          setProgress('Aucune PR fusionnée trouvée pour ces auteurs');
          setLoading(false);
          return;
        }

        setProgress(`Traitement de ${prs.length} PRs...`);

        // Calculer le temps moyen de merge pour chaque auteur
        const timeByAuthor = {};
        const prsByAuthor = {};
        const prDetailsData = [];

        prs.forEach(pr => {
          const createdDate = new Date(pr.creationDate);
          const closedDate = new Date(pr.closedDate);
          
          // Calculer le temps en jours
          const timeInDays = (closedDate - createdDate) / (1000 * 60 * 60 * 24);
          
          if (timeInDays >= 0) {
            const author = pr.createdBy;
            
            if (!timeByAuthor[author]) {
              timeByAuthor[author] = [];
              prsByAuthor[author] = [];
            }
            
            timeByAuthor[author].push(timeInDays);
            prsByAuthor[author].push(pr);
            
            prDetailsData.push({
              id: pr.id,
              title: pr.title,
              author: author,
              authorEmail: pr.createdByEmail,
              createdDate: createdDate.toLocaleDateString('fr-FR'),
              mergedDate: closedDate.toLocaleDateString('fr-FR'),
              timeToMerge: timeInDays.toFixed(1),
              commits: pr.commits,
              reviewers: pr.reviewers,
              url: `https://dev.azure.com/chapsvision/Fabrique/_git/${REPO_NAME}/pullrequest/${pr.id}`
            });
          }
        });

        setProgress('Calcul des statistiques...');

        // Calculer les moyennes par auteur
        const authorStats = {};
        const authorLabels = [];
        const authorAverages = [];
        const authorCounts = [];

        Object.keys(timeByAuthor).forEach(author => {
          const times = timeByAuthor[author];
          const avg = times.reduce((a, b) => a + b, 0) / times.length;
          const min = Math.min(...times);
          const max = Math.max(...times);

          authorStats[author] = {
            average: avg.toFixed(1),
            min: min.toFixed(1),
            max: max.toFixed(1),
            count: times.length,
            totalTime: times.reduce((a, b) => a + b, 0).toFixed(1)
          };

          authorLabels.push(author);
          authorAverages.push(parseFloat(avg.toFixed(1)));
          authorCounts.push(times.length);
        });

        // Calculer la moyenne globale
        const allTimes = Object.values(timeByAuthor).flat();
        const globalAverage = allTimes.reduce((a, b) => a + b, 0) / allTimes.length;

        setStats({
          globalAverage: globalAverage.toFixed(1),
          totalPrs: prs.length,
          authors: authorStats
        });

        // Créer le graphique
        setChartData({
          labels: authorLabels,
          datasets: [
            {
              label: 'Temps moyen de merge (jours)',
              data: authorAverages,
              backgroundColor: 'rgba(59, 130, 246, 0.7)',
              borderColor: 'rgba(59, 130, 246, 1)',
              borderWidth: 1,
            },
          ],
        });

        // Trier les PRs par temps de merge décroissant
        prDetailsData.sort((a, b) => parseFloat(b.timeToMerge) - parseFloat(a.timeToMerge));
        setPrDetails(prDetailsData);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur récupération données PRs:', error);
        setProgress('Erreur lors de la récupération des données');
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">MTTM - Squad Socle Front (Temps de Merge des PRs)</h2>
      
      {loading || !stats || !chartData ? (
        <div>
          <div className="text-gray-600">Chargement...</div>
          {progress && <div className="text-sm text-gray-500 mt-2">{progress}</div>}
        </div>
      ) : (
        <div>
          {/* Résumé global */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Temps moyen de merge (global)</div>
              <div className="text-2xl font-bold text-blue-600">{stats.globalAverage}</div>
              <div className="text-xs text-gray-500">jours</div>
            </div>
            
            <div className="bg-purple-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Total de PRs fusionnées</div>
              <div className="text-2xl font-bold text-purple-600">{stats.totalPrs}</div>
              <div className="text-xs text-gray-500">6 derniers mois</div>
            </div>
            
            <div className="bg-indigo-50 rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Nombre de contributeurs</div>
              <div className="text-2xl font-bold text-indigo-600">{Object.keys(stats.authors).length}</div>
              <div className="text-xs text-gray-500">Squad Socle Front</div>
            </div>
          </div>

          {/* Graphique par auteur */}
          <div className="mb-8">
            <h3 className="text-md font-semibold mb-4">Temps moyen par contributeur</h3>
            <Bar
              data={chartData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  title: {
                    display: false
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y;
                        return `${value} jours`;
                      }
                    }
                  }
                },
                scales: {
                  x: { 
                    title: { display: true, text: 'Contributeur' },
                    ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 }
                  },
                  y: { 
                    title: { display: true, text: 'Temps (jours)' }, 
                    beginAtZero: true 
                  },
                },
              }}
            />
          </div>

          {/* Statistiques détaillées par auteur */}
          <div className="mb-8">
            <h3 className="text-md font-semibold mb-4">Détail par contributeur</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Object.entries(stats.authors).map(([author, authorStat]) => (
                <div key={author} className="border rounded p-4">
                  <div className="font-semibold text-gray-900 mb-3">{author}</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Moyenne</div>
                      <div className="text-lg font-bold text-blue-600">{authorStat.average}</div>
                      <div className="text-xs text-gray-500">jours</div>
                    </div>
                    <div>
                      <div className="text-gray-600">PRs</div>
                      <div className="text-lg font-bold text-purple-600">{authorStat.count}</div>
                      <div className="text-xs text-gray-500">fusionnées</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Min</div>
                      <div className="text-lg font-bold text-green-600">{authorStat.min}</div>
                      <div className="text-xs text-gray-500">jours</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Max</div>
                      <div className="text-lg font-bold text-red-600">{authorStat.max}</div>
                      <div className="text-xs text-gray-500">jours</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Détail des PRs */}
          {prDetails.length > 0 && (
            <div>
              <h3 className="text-md font-semibold mb-3">Détail des PRs</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="px-4 py-2 text-left">ID</th>
                      <th className="px-4 py-2 text-left">Titre</th>
                      <th className="px-4 py-2 text-left">Auteur</th>
                      <th className="px-4 py-2 text-center">Date création</th>
                      <th className="px-4 py-2 text-center">Date fusion</th>
                      <th className="px-4 py-2 text-center font-bold text-blue-600">MTTM (jours)</th>
                      <th className="px-4 py-2 text-center">Commits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prDetails.map((pr) => (
                      <tr key={pr.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <a 
                            href={pr.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline font-medium"
                          >
                            #{pr.id}
                          </a>
                        </td>
                        <td className="px-4 py-2 max-w-xs truncate" title={pr.title}>{pr.title}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{pr.author}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{pr.createdDate}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{pr.mergedDate}</td>
                        <td className="px-4 py-2 text-center font-bold text-blue-600">{pr.timeToMerge}</td>
                        <td className="px-4 py-2 text-center text-gray-600">{pr.commits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
